/**
 * Circle dispersal pattern.
 *
 * Arranges markers within the smallest circle possible, centered on
 * the geometric mean of their original positions, such that no bounding
 * boxes overlap.
 *
 * Special cases:
 * - 1 marker: no offset
 * - 2 markers: offset along the line connecting them (or vertically if coincident)
 * - N markers: angular distribution around the centroid. One marker may be
 *   placed at the center if it fits without overlapping the ring markers.
 */

import {
  centroid,
  angleBetween,
  positionOnCircle,
  smallestNonOverlappingRadius,
  distance,
} from '../geometry.js';

/**
 * Compute dispersal offsets for a group of overlapping markers using
 * the circle pattern.
 *
 * @param {import('../DisperseEngine.js').MarkerDescriptor[]} group
 * @param {number} spacing - Minimum gap between bounding boxes (px)
 * @returns {Map<string|number, import('../DisperseEngine.js').MarkerOffset>}
 */
export function circlePattern(group, spacing) {
  const offsets = new Map();

  if (group.length === 0) {
    return offsets;
  }

  if (group.length === 1) {
    offsets.set(group[0].id, { dx: 0, dy: 0 });
    return offsets;
  }

  const center = centroid(group.map((m) => ({ x: m.x, y: m.y })));

  if (group.length === 2) {
    return disperseTwoMarkers(group, center, spacing);
  }

  return disperseNMarkers(group, center, spacing);
}

/**
 * Special case for exactly two markers: offset them along the line
 * connecting them (or vertically if coincident).
 */
function disperseTwoMarkers(group, center, spacing) {
  const offsets = new Map();
  const [m1, m2] = group;

  // Determine the axis of dispersal
  let angle;
  if (m1.x === m2.x && m1.y === m2.y) {
    // Coincident: disperse vertically (arbitrary but visually clean)
    angle = -Math.PI / 2; // up
  } else {
    angle = angleBetween(center, { x: m1.x, y: m1.y });
  }

  // Binary search for the minimum distance from center along this axis
  const markers = [m1, m2];
  const angles = [angle, angle + Math.PI];
  const radius = smallestNonOverlappingRadius(markers, center, angles, spacing);

  const p1 = positionOnCircle(center, radius, angles[0]);
  const p2 = positionOnCircle(center, radius, angles[1]);

  offsets.set(m1.id, { dx: p1.x - m1.x, dy: p1.y - m1.y });
  offsets.set(m2.id, { dx: p2.x - m2.x, dy: p2.y - m2.y });

  return offsets;
}

/**
 * General case for N >= 3 markers: assign each an angular slot on a circle,
 * find the minimum radius. Optionally place one marker at center if it fits.
 */
function disperseNMarkers(group, center, spacing) {
  const offsets = new Map();
  const N = group.length;
  const TWO_PI = 2 * Math.PI;

  // Compute each marker's angle from the centroid. Markers coincident with
  // the centroid (or with each other away from the centroid) get null.
  const markersWithAngle = group.map((m) => {
    const dist = distance(center, { x: m.x, y: m.y });
    const angle = dist < 0.001 ? null : angleBetween(center, { x: m.x, y: m.y });
    return { marker: m, angle, dist };
  });

  const withAngle = markersWithAngle.filter((m) => m.angle !== null);
  const withoutAngle = markersWithAngle.filter((m) => m.angle === null);

  // Determine the anchor angle for stop positions. Use the first marker
  // that has a real angle; fall back to 0 if all are coincident.
  const anchorAngle = withAngle.length > 0 ? withAngle[0].angle : 0;

  // Generate N evenly-spaced stop positions starting from the anchor angle.
  const stepAngle = TWO_PI / N;
  const stops = Array.from({ length: N }, (_, i) => {
    let angle = anchorAngle + i * stepAngle;
    if (angle >= TWO_PI) angle -= TWO_PI;
    return { angle, occupied: false };
  });

  // Helper: find the nearest unoccupied stop for a given angle. Distance
  // is measured as the shortest arc on the circle.
  function claimNearestStop(targetAngle) {
    let bestIdx = -1;
    let bestDist = Infinity;
    for (let i = 0; i < stops.length; i++) {
      if (stops[i].occupied) continue;
      let d = Math.abs(stops[i].angle - targetAngle);
      if (d > Math.PI) d = TWO_PI - d;
      if (d < bestDist) {
        bestDist = d;
        bestIdx = i;
      }
    }
    if (bestIdx !== -1) stops[bestIdx].occupied = true;
    return bestIdx;
  }

  // Phase 1: assign markers that have real angles to their nearest stop.
  const assignedAngles = [];

  // Sort markers with angles so that those closest to a stop get first pick.
  // This avoids a later marker "stealing" a closer stop from an earlier one.
  const sortedWithAngle = [...withAngle].sort((a, b) => {
    // Sort by minimum angular distance to any stop
    const minDistA = Math.min(
      ...stops.map((s) => {
        let d = Math.abs(s.angle - a.angle);
        return d > Math.PI ? TWO_PI - d : d;
      })
    );
    const minDistB = Math.min(
      ...stops.map((s) => {
        let d = Math.abs(s.angle - b.angle);
        return d > Math.PI ? TWO_PI - d : d;
      })
    );
    return minDistA - minDistB;
  });

  for (const entry of sortedWithAngle) {
    const stopIdx = claimNearestStop(entry.angle);
    assignedAngles.push({
      marker: entry.marker,
      angle: stops[stopIdx].angle,
    });
  }

  // Phase 2: assign markers without angles to remaining unoccupied stops.
  for (const entry of withoutAngle) {
    // Find the first unoccupied stop
    const stopIdx = stops.findIndex((s) => !s.occupied);
    if (stopIdx !== -1) {
      stops[stopIdx].occupied = true;
      assignedAngles.push({
        marker: entry.marker,
        angle: stops[stopIdx].angle,
      });
    }
  }

  const markers = assignedAngles.map((a) => a.marker);
  const angles = assignedAngles.map((a) => a.angle);
  const radius = smallestNonOverlappingRadius(markers, center, angles, spacing);

  for (let i = 0; i < markers.length; i++) {
    const pos = positionOnCircle(center, radius, angles[i]);
    offsets.set(markers[i].id, {
      dx: pos.x - markers[i].x,
      dy: pos.y - markers[i].y,
    });
  }

  return offsets;
}
